import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useWebSocket from '../../hooks/useWebSocket';
import useWakeLock from '../../hooks/useWakeLock';
import logoIcon from '../../assets/images/standalone_logo.png';

/**
 * Challenges Page - Participant
 * Submit challenges and issues faced during the training
 */

const ChallengesPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const socket = useWebSocket(sessionId);
  // Fix #9: keep screen awake while submitting challenges during a live session
  useWakeLock(true);
  const [session, setSession] = useState(null);
  const [challenge, setChallenge] = useState('');
  const [actionPlan, setActionPlan] = useState('');
  const [submittedChallenges, setSubmittedChallenges] = useState([]);
  const [submittedActionPlans, setSubmittedActionPlans] = useState([]);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showSubmitActionPlanConfirm, setShowSubmitActionPlanConfirm] = useState(false);
  const [showActivityStartedDialog, setShowActivityStartedDialog] = useState(false);
  const [activityInfo, setActivityInfo] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Navigation items for sidebar
  const navigationItems = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/lobby`),
    },
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/notes`),
    },
    {
      id: 'workbooks',
      name: 'Workbooks',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/workbooks`),
    },
    {
      id: 'activities',
      name: 'Aktivities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      badge: true,
      onClick: () => navigate(`/session/${sessionId}/active`),
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/reports`),
    },
    {
      id: 'challenges',
      name: 'Challenges',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      onClick: () => {},
    },
    {
      id: 'help',
      name: 'Help Center',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  useEffect(() => {
    // Load session data from localStorage
    const storedSession = localStorage.getItem('current_session');
    if (storedSession) {
      const parsedSession = JSON.parse(storedSession);
      setSession(parsedSession);
    }

    // Load saved challenges from localStorage
    const savedChallenges = localStorage.getItem(`session-${sessionId}-challenges`);
    if (savedChallenges) {
      setSubmittedChallenges(JSON.parse(savedChallenges));
    }

    // Load saved action plans from localStorage
    const savedActionPlans = localStorage.getItem(`session-${sessionId}-actionplans`);
    if (savedActionPlans) {
      setSubmittedActionPlans(JSON.parse(savedActionPlans));
    }
  }, [sessionId]);

  // Listen for activity launched events
  useEffect(() => {
    if (!socket || !sessionId) return;

    const handleActivityLaunched = (data) => {
      console.log('[Challenges] Activity launched:', data);
      setActivityInfo(data);
      setShowActivityStartedDialog(true);
    };

    socket.on('activity:launched', handleActivityLaunched);

    return () => {
      socket.off('activity:launched', handleActivityLaunched);
    };
  }, [socket, sessionId]);

  const handleGoToActivity = () => {
    setShowActivityStartedDialog(false);
    navigate(`/session/${sessionId}/active`);
  };

  const handleSubmit = () => {
    if (!challenge.trim()) return;

    const newChallenge = {
      id: Date.now(),
      text: challenge,
      timestamp: new Date().toISOString(),
    };

    const updatedChallenges = [...submittedChallenges, newChallenge];
    setSubmittedChallenges(updatedChallenges);
    localStorage.setItem(`session-${sessionId}-challenges`, JSON.stringify(updatedChallenges));

    setChallenge('');
    setShowSubmitConfirm(false);
  };

  const handleDeleteChallenge = (challengeId) => {
    const updatedChallenges = submittedChallenges.filter((c) => c.id !== challengeId);
    setSubmittedChallenges(updatedChallenges);
    localStorage.setItem(`session-${sessionId}-challenges`, JSON.stringify(updatedChallenges));
  };

  const handleSubmitActionPlan = () => {
    if (!actionPlan.trim()) return;

    const newActionPlan = {
      id: Date.now(),
      text: actionPlan,
      timestamp: new Date().toISOString(),
    };

    const updatedActionPlans = [...submittedActionPlans, newActionPlan];
    setSubmittedActionPlans(updatedActionPlans);
    localStorage.setItem(`session-${sessionId}-actionplans`, JSON.stringify(updatedActionPlans));

    setActionPlan('');
    setShowSubmitActionPlanConfirm(false);
  };

  const handleDeleteActionPlan = (actionPlanId) => {
    const updatedActionPlans = submittedActionPlans.filter((a) => a.id !== actionPlanId);
    setSubmittedActionPlans(updatedActionPlans);
    localStorage.setItem(`session-${sessionId}-actionplans`, JSON.stringify(updatedActionPlans));
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Sidebar - Desktop */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 fixed left-0 top-0 h-full z-40"
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center justify-between">
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2"
                >
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <span className="text-lg font-bold text-neutral-900">Aktivity</span>
                </motion.div>
              )}
            </AnimatePresence>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    item.id === 'challenges'
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {item.id === 'challenges' && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${item.id === 'challenges' ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        className="font-medium whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {item.badge && !sidebarCollapsed && (
                    <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700 rounded-full">
                      Live
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </motion.aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} transition-all duration-300`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-neutral-200">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 lg:hidden">
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <span className="text-lg font-bold text-neutral-900">Aktivity</span>
            </div>

            {/* Session Info */}
            <div className="hidden lg:flex items-center gap-3">
              <h1 className="text-lg font-semibold text-neutral-900">Challenges & Action Plans</h1>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 rounded-md">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-700">Live</span>
              </div>
              {(session?.sessionCode || session?.code) && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{session.sessionCode || session.code}</span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 lg:p-6 pb-32 lg:pb-6">
          <div className="max-w-4xl mx-auto">
            {/* Page Header */}
            <div className="mb-6 lg:hidden">
              <h1 className="text-2xl font-bold text-neutral-900 mb-2">Challenges & Action Plans</h1>
              <p className="text-neutral-600">
                Share challenges you're facing and outline your action plans for improvement.
              </p>
            </div>

            <div className="hidden lg:block mb-6">
              <p className="text-neutral-600">
                Share challenges you're facing and outline your action plans for improvement.
              </p>
            </div>

            {/* Submit Challenge Card */}
            <Card className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-neutral-900">Submit a Challenge</h3>
                  <p className="text-sm text-neutral-600">Describe any difficulty you're experiencing</p>
                </div>
              </div>

              <textarea
                value={challenge}
                onChange={(e) => setChallenge(e.target.value)}
                placeholder="Describe your challenge here...&#10;&#10;Example: I'm having trouble understanding the concept of..."
                className="w-full h-32 bg-white text-neutral-900 placeholder-neutral-400 rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 border border-neutral-300 mb-4"
              />

              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-500">{challenge.length} characters</span>
                <Button
                  onClick={() => setShowSubmitConfirm(true)}
                  disabled={!challenge.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  Submit Challenge
                </Button>
              </div>
            </Card>

            {/* Submitted Challenges */}
            {submittedChallenges.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-bold text-neutral-900 mb-4">Your Submitted Challenges</h2>
                <div className="space-y-3">
                  {submittedChallenges.map((item) => (
                    <Card key={item.id} padding="md">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-neutral-900 mb-2">{item.text}</p>
                          <span className="text-xs text-neutral-500">
                            Submitted on {new Date(item.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteChallenge(item.id)}
                          className="text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                          aria-label="Delete challenge"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Action Plan Card */}
            <Card className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-neutral-900">Submit an Action Plan</h3>
                  <p className="text-sm text-neutral-600">What steps will you take to improve?</p>
                </div>
              </div>

              <textarea
                value={actionPlan}
                onChange={(e) => setActionPlan(e.target.value)}
                placeholder="Describe your action plan here...&#10;&#10;Example: I will practice daily for 30 minutes to improve my skills in..."
                className="w-full h-32 bg-white text-neutral-900 placeholder-neutral-400 rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 border border-neutral-300 mb-4"
              />

              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-500">{actionPlan.length} characters</span>
                <Button
                  onClick={() => setShowSubmitActionPlanConfirm(true)}
                  disabled={!actionPlan.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Submit Action Plan
                </Button>
              </div>
            </Card>

            {/* Submitted Action Plans */}
            {submittedActionPlans.length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-bold text-neutral-900 mb-4">Your Submitted Action Plans</h2>
                <div className="space-y-3">
                  {submittedActionPlans.map((item) => (
                    <Card key={item.id} padding="md">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-neutral-900 mb-2">{item.text}</p>
                          <span className="text-xs text-neutral-500">
                            Submitted on {new Date(item.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteActionPlan(item.id)}
                          className="text-red-600 hover:text-red-700 transition-colors flex-shrink-0"
                          aria-label="Delete action plan"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Info Section */}
            <Card padding="md" className="bg-blue-50 border-blue-200">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">About This Page</h4>
                  <p className="text-sm text-blue-800">
                    Share challenges you're facing and create action plans to overcome them. Your trainer will review both to provide personalized guidance and support.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Show all items with smaller sizes */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                item.id === 'activities' ? '' : 'text-neutral-500'
              }`}
              style={item.id === 'activities' ? { color: '#ff8a00' } : {}}
            >
              <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                {item.icon}
                {item.badge && (
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Submit Challenge Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSubmitConfirm}
        onClose={() => setShowSubmitConfirm(false)}
        onConfirm={handleSubmit}
        title="Submit Challenge?"
        message="Are you sure you want to submit this challenge? Your trainer will be notified."
        confirmText="Submit"
        cancelText="Cancel"
        variant="info"
      />

      {/* Submit Action Plan Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSubmitActionPlanConfirm}
        onClose={() => setShowSubmitActionPlanConfirm(false)}
        onConfirm={handleSubmitActionPlan}
        title="Submit Action Plan?"
        message="Are you sure you want to submit this action plan? Your trainer will be notified."
        confirmText="Submit"
        cancelText="Cancel"
        variant="info"
      />

      {/* Activity Started Dialog */}
      <ConfirmDialog
        isOpen={showActivityStartedDialog}
        onClose={() => setShowActivityStartedDialog(false)}
        onConfirm={handleGoToActivity}
        title={`${activityInfo?.activityType === 'poll' ? 'Poll' : activityInfo?.activityType === 'quiz' ? 'Quiz' : 'Activity'} Started!`}
        message={`${activityInfo?.activityTitle || 'An activity'} has been launched. Go back to activities to participate.`}
        confirmText="Go to Activity"
        cancelText="Stay Here"
        variant="info"
      />
    </div>
  );
};

export default ChallengesPage;
